import React, { useState, useRef } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Grid,
  Paper
} from "@mui/material";
import PersonalDetailsStep from "./steps/PersonalDetailsStep";
import BankDetailsStep from "./steps/BankDetailsStep";
import EducationExperienceStep from "./steps/EducationExperienceStep";
import { useDispatch } from "react-redux";
import AuthViewController from "../../redux/actions/AuthViewController";
import { useNavigate, useParams } from "react-router-dom";

const steps = [
  "Personal Details",
  "Bank & Tax Details",
  "Education & Experience"
];

const initialValues = {
  profile_image: null,
  date_of_birth: "",
  address: {
    residentialAddress: "",
    currentAddress: ""
  },
  mobile_number: "",
  alternative_mobile_number: "",
  aadhar_number: "",
  about: "",
  gender: "",
  nationality: "",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  pan_number: "",
  pf_number: "",
  esi_number: "",
  education: [
    {
      name: "",
      percentage: "",
      passingYear: "",
      doc: null
    }
  ],
  experience: [
    {
      company: "",
      joining: "",
      end: "",
      designation: "",
      doc: null
    }
  ]
};

const validationSchemas = [
  Yup.object({
    date_of_birth: Yup.string().required("Date of birth is required"),
    mobile_number: Yup.string().required("Mobile Number is required"),
    address: Yup.object({
      residentialAddress: Yup.string().required(
        "Residential address is required"
      ),
      currentAddress: Yup.string().required("Current address is required")
    }),
    profile_image: Yup.mixed()
      .required("Profile image is required")
      .test("fileType", "Only image files are allowed", (value) => {
        return value && value.type && value.type.startsWith("image/");
      }),
    aadhar_number: Yup.string()
      .required("Aadhar number is required")
      .matches(/^\d{12}$/, "Aadhar number must be 12 digits"),

    gender: Yup.string()
      .required("Gender is required")
      .oneOf(["male", "female", "other","prefer_not_to_say"], "Invalid gender"),

    nationality: Yup.string()
      .required("Nationality is required")
      
  }),
  Yup.object({
    bank_name: Yup.string().required("Bank Name is required"),
    account_number: Yup.string().required("Account Number is required"),
    ifsc_code: Yup.string().required("IFSC Code is required"),
    pan_number: Yup.string(),
    pf_number: Yup.string(),
    esi_number: Yup.string()
  }),
  Yup.object({
    education: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("College/Institute Name is required"),
          percentage: Yup.string().required("Percentage is required"),
          passingYear: Yup.string().required("Passing Year is required"),
          doc: Yup.mixed().required("Education document is required")
        })
      )
      .min(1, "At least one education entry is required")
  })
];

const EmployeeSelfOnboardingForm = () => {
  const { submitOnboardingForm } = AuthViewController();
  const { uid, token } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const contentRef = useRef(null); // 👈 add ref

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleFinalSubmit = (values) => {
    const formData = new FormData();

    // Flat fields
    formData.append("profile_image", values.profile_image);
    formData.append("date_of_birth", values.date_of_birth);
    formData.append("aadhar_number", values.aadhar_number);
    formData.append("about", values.about);
    formData.append("bank_name", values.bank_name);
    formData.append("account_number", values.account_number);
    formData.append("ifsc_code", values.ifsc_code);
    formData.append("pan_number", values.pan_number);
    formData.append("pf_number", values.pf_number);
    formData.append("esi_number", values.esi_number);
    formData.append("gender", values.gender);
    formData.append("nationality", values.nationality);
    formData.append("mobile_number", values.mobile_number);
    formData.append("alternative_mobile_number", values.alternative_mobile_number);

    // Address
    formData.append("residentialAddress", values.address.residentialAddress);
    formData.append("currentAddress", values.address.currentAddress);

    // Education
    const educationData = values.education.map(({ doc, ...rest }) => rest);
    formData.append("education", JSON.stringify(educationData));
    values.education.forEach((edu, index) => {
      if (edu.doc) {
        formData.append(`education_docs[${index}]`, edu.doc);
      }
    });

    // Experience
    const experienceData = values.experience.map(({ doc, ...rest }) => rest);
    formData.append("experience", JSON.stringify(experienceData));
    values.experience.forEach((exp, index) => {
      if (exp.doc) {
        formData.append(`experience_docs[${index}]`, exp.doc);
      }
    });

    // Redux action
    dispatch(submitOnboardingForm(uid, token, formData, navigate));
  };

  const renderStepContent = (stepProps) => {
    switch (activeStep) {
      case 0:
        return <PersonalDetailsStep {...stepProps} />;
      case 1:
        return <BankDetailsStep {...stepProps} />;
      case 2:
        return <EducationExperienceStep {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div
        style={{
          backgroundColor: "#fff",
          color: "#1976d2",
          padding: "20px 0",
          textAlign: "center",
          // boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          zIndex: 10,
          position: "relative",
                              borderBottom: "1px solid #e5e7eb",

        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Employee Self-Onboarding
        </Typography>
      </div>

      {/* Formik Wrapper */}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchemas[activeStep]}
        onSubmit={(values) => handleFinalSubmit(values)}
      >
        {(formikProps) => {
          const { values, validateForm, setSubmitting, setTouched } =
            formikProps;

          const handleStepSubmit = async () => {
            const errors = await validateForm();

            if (Object.keys(errors).length > 0) {
              // build touched tree
              const buildTouched = (errObj) => {
                if (typeof errObj !== "object" || errObj === null) return true;
                return Object.keys(errObj).reduce((acc, key) => {
                  acc[key] = buildTouched(errObj[key]);
                  return acc;
                }, {});
              };
              setTouched(buildTouched(errors), true);
              setSubmitting(false);
              // 👇 scroll to top of content on error
              if (contentRef.current) {
                contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
              }

              return;
            }

            if (activeStep === steps.length - 1) {
              handleFinalSubmit(values);
            } else {
              setTouched({});
              handleNext();
              // 👇 scroll to top of content on error
              if (contentRef.current) {
                contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
              }
            }
            setSubmitting(false);
          };

          return (
            <Form style={{ flex: 1 }}>
              <Grid container style={{ height: "100%" }}>
                {/* Stepper Sidebar */}
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    backgroundColor: "#fff",
                    borderRight: "1px solid #e5e7eb",
                    // boxShadow: "2px 0 8px rgba(0, 0, 0, 0.1)",
                    position: "sticky",
                    top: 80,
                    alignSelf: "flex-start",
                    height: "100%",
                    overflowY: "auto",
                    padding: "24px"
                  }}
                >
                  <Stepper activeStep={activeStep} orientation="vertical">
                    {steps.map((label) => (
                      <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Grid>

                {/* Content Area */}
                <Grid
                  ref={contentRef}
                  item
                  xs={12}
                  md={9}
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    maxHeight: "calc(100vh - 80px)", // 80px = header height
                    bgcolor: "#fff"
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      p: 4,
                      height: "100%",
                      justifyContent: "space-between",
                      bgcolor: "#fff"
                    }}
                  >
                    {renderStepContent(formikProps)}

                    <Grid container justifyContent="space-between" mt={4}>
                      <Grid item>
                        <Button
                          variant="outlined"
                          onClick={handleBack}
                          disabled={activeStep === 0}
                        >
                          Back
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button
                          variant="contained"
                          onClick={handleStepSubmit}
                          type="button"
                        >
                          {activeStep === steps.length - 1 ? "Submit" : "Next"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default EmployeeSelfOnboardingForm;
